import React, { useMemo } from 'react';
import { ArrowLeft, ArrowRight, Copy, Trash2, Plus } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { TYPES, UPPERS, makeModule } from './catalog';
import { WALL_KEYS, WALL_LABELS } from './layout';
import { ModuleFront, makeCtx } from './parts';
import { MatDefs } from './defs';

const cm = (mm) => `${Math.round(mm / 10)} cm`;

function Thumb({ type, mats }) {
  const spec = TYPES[type];
  const p = `th-${type}`;
  const c = useMemo(() => makeCtx(p, mats, true, true), [p, mats]);
  const w = spec.w, h = spec.h + (spec.elev || 0);
  const it = { spec, w, h: spec.h, idx: 1, elev: spec.elev || 0 };
  return (
    <svg viewBox={`-60 -420 ${w + 120} ${Math.max(h, 900) + 480}`} className="w-full h-20" preserveAspectRatio="xMidYMax meet">
      <MatDefs p={p} mats={mats} />
      <g transform={`translate(0 ${Math.max(h, 900) - h})`}><ModuleFront it={it} c={c} /></g>
    </svg>
  );
}

const IconBtn = ({ onClick, children, label, id, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={id}
    className="w-9 h-9 flex items-center justify-center border border-white/10 text-gray-300 hover:border-[#a68966] hover:text-[#a68966] disabled:opacity-30 transition-colors duration-200">
    {children}
  </button>
);

function SelectedEditor({ m, i, n, cat, update, setSel, k, side }) {
  const spec = TYPES[m.type];
  const set = (patch) => update((cfg) => ({ ...cfg, [k]: cfg[k].map((x) => (x.uid === m.uid ? { ...x, ...patch } : x)) }));
  const move = (dir) => update((cfg) => { const a = [...cfg[k]]; const j = i + dir; [a[i], a[j]] = [a[j], a[i]]; return { ...cfg, [k]: a }; });
  const dup = () => update((cfg) => { const a = [...cfg[k]]; a.splice(i + 1, 0, { ...m, uid: makeModule(m.type).uid }); return { ...cfg, [k]: a }; });
  const del = () => { update((cfg) => ({ ...cfg, [k]: cfg[k].filter((x) => x.uid !== m.uid) })); setSel(null); };
  const canUpper = spec.zone !== 'tall' && cat.uppers.length > 1;
  const uppers = side ? cat.uppers.filter((u) => u !== 'hood') : cat.uppers;
  const types = side ? cat.types.filter((t) => TYPES[t].zone !== 'free') : cat.types;
  return (
    <div className="px-4 pb-4 pt-1 space-y-4 animate-in fade-in slide-in-from-top-1 duration-300" data-testid="selected-module-editor">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Type d'élément</p>
        <select value={m.type} onChange={(e) => set({ type: e.target.value, w: TYPES[e.target.value].w })} data-testid="module-type-select"
          className="w-full bg-[#111] border border-white/10 text-gray-200 text-sm px-3 py-2 focus:border-[#a68966] outline-none">
          {types.map((t) => <option key={t} value={t}>{TYPES[t].label}</option>)}
        </select>
      </div>
      <div>
        <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3"><span>Largeur</span><span className="text-[#d9c3a0] text-xs tracking-normal">{cm(m.w)}</span></div>
        <Slider value={[m.w]} min={spec.wMin} max={spec.wMax} step={50} onValueChange={([v]) => set({ w: v })} data-testid="module-width-slider" />
      </div>
      {canUpper && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Élément haut</p>
          <div className="flex flex-wrap gap-2">
            {uppers.map((u) => (
              <button key={u} type="button" onClick={() => set({ upper: u })} data-testid={`upper-option-${u}`}
                className={`text-xs px-3 py-1.5 border transition-colors duration-200 ${m.upper === u ? 'border-[#a68966] bg-[#a68966]/15 text-[#e6d3b3]' : 'border-white/10 text-gray-400 hover:border-white/30'}`}>
                {UPPERS[u].label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <IconBtn onClick={() => move(-1)} disabled={i === 0} label="Déplacer à gauche" id="module-move-left"><ArrowLeft className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => move(1)} disabled={i === n - 1} label="Déplacer à droite" id="module-move-right"><ArrowRight className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={dup} label="Dupliquer" id="module-duplicate"><Copy className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={del} label="Supprimer" id="module-delete"><Trash2 className="w-4 h-4" /></IconBtn>
      </div>
    </div>
  );
}

export function ModulesPanel({ cfg, cat, update, sel, setSel, wall = 'A', setWall, walls = ['A'], offset = 0 }) {
  const k = WALL_KEYS[wall];
  const list = cfg[k] || [];
  const side = wall !== 'A';
  const types = side ? cat.types.filter((t) => TYPES[t].zone !== 'free') : cat.types;
  const add = (t) => {
    const nm = makeModule(t, side && TYPES[t].upper === 'hood' ? 'none' : undefined);
    update((c) => {
      const a = [...(c[k] || [])]; const i = a.findIndex((x) => x.uid === sel);
      a.splice(i >= 0 ? i + 1 : a.length, 0, nm);
      return { ...c, [k]: a };
    });
    setSel(nm.uid);
  };
  return (
    <div className="space-y-8">
      {walls.length > 1 && (
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${walls.length}, 1fr)` }} data-testid="wall-tabs">
          {walls.map((wk) => (
            <button key={wk} type="button" onClick={() => { setWall(wk); setSel(null); }} data-testid={`wall-tab-${wk}`}
              className={`py-2.5 text-[10px] uppercase tracking-[0.2em] border transition-colors ${wall === wk ? 'border-[#a68966] bg-[#a68966]/15 text-[#e6d3b3]' : 'border-white/10 text-gray-500 hover:text-gray-300'}`}>
              {WALL_LABELS[wk]}
            </button>
          ))}
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966] mb-3">{walls.length > 1 ? `${WALL_LABELS[wall]} · ` : 'Composition · '}{list.length} éléments</p>
        {list.length === 0 && <p className="text-xs text-gray-500 border border-dashed border-white/10 p-4" data-testid="wall-empty">Ce mur est vide : ajoutez vos premiers éléments ci-dessous.</p>}
        <ol className="border border-white/10 divide-y divide-white/10" data-testid="modules-list">
          {list.map((m, i) => (
            <li key={m.uid} className={sel === m.uid ? 'bg-white/[0.04]' : ''}>
              <button type="button" onClick={() => setSel(sel === m.uid ? null : m.uid)} data-testid={`module-row-${i}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-left group">
                <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-playfair border transition-colors duration-200 ${sel === m.uid ? 'bg-[#4dffb8] border-[#4dffb8] text-[#1a1a1a]' : 'border-[#a68966]/60 text-[#d9c3a0]'}`}>{offset + i + 1}</span>
                <span className="flex-1 text-sm text-gray-200 group-hover:text-white truncate">{TYPES[m.type].label}</span>
                <span className="text-xs text-gray-500">{cm(m.w)}</span>
              </button>
              {sel === m.uid && <SelectedEditor m={m} i={i} n={list.length} cat={cat} update={update} setSel={setSel} k={k} side={side} />}
            </li>
          ))}
        </ol>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#a68966] mb-3">Ajouter un élément</p>
        <div className="grid grid-cols-3 gap-2" data-testid="add-module-grid">
          {types.map((t) => (
            <button key={t} type="button" onClick={() => add(t)} data-testid={`add-module-${t}`}
              className="group relative border border-white/10 bg-[#f6f1e8]/[0.03] hover:border-[#a68966] p-2 pt-3 transition-colors duration-200 text-left">
              <Thumb type={t} mats={cfg.mats} />
              <span className="block mt-1 text-[10px] leading-tight text-gray-400 group-hover:text-[#e6d3b3]">{TYPES[t].label}</span>
              <Plus className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-[#a68966] opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </div>
      {cat.island.allowed && <IslandEditor cfg={cfg} cat={cat} update={update} active={sel === 'island'} />}
    </div>
  );
}

function IslandEditor({ cfg, cat, update, active }) {
  const set = (patch) => update((c) => ({ ...c, island: { ...c.island, ...patch } }));
  return (
    <div className={`border p-4 space-y-4 transition-colors duration-300 ${active ? 'border-[#4dffb8]/70' : 'border-white/10'}`} data-testid="island-editor">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-200">{cat.island.label}</p>
        <Switch checked={cfg.island.on} onCheckedChange={(v) => set({ on: v })} data-testid="island-toggle" />
      </div>
      {cfg.island.on && (
        <>
          <div>
            <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3"><span>Longueur</span><span className="text-[#d9c3a0] text-xs tracking-normal">{cm(cfg.island.w)}</span></div>
            <Slider value={[cfg.island.w]} min={900} max={3600} step={50} onValueChange={([v]) => set({ w: v })} data-testid="island-width-slider" />
          </div>
          <div>
            <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3"><span>Profondeur</span><span className="text-[#d9c3a0] text-xs tracking-normal">{cm(cfg.island.d)}</span></div>
            <Slider value={[cfg.island.d]} min={500} max={1400} step={50} onValueChange={([v]) => set({ d: v })} data-testid="island-depth-slider" />
          </div>
        </>
      )}
    </div>
  );
}
